import { bindActionCreators } from 'redux';
import { createAction, handleActions } from 'redux-actions';
import cloneDeep from 'lodash.clonedeep';
import { MODE } from 'constants/mode';
import { find, findIndex } from 'utils/array';
import { findTargetByPath } from '../utils/pathParser';
import Event from 'model/Event';
import errorSvc from 'services/errorService';
import eventGroupSvc from 'services/eventGroupService';
import pageviewActionType from 'redux/modules/pageview/pageviewActionType';
import * as notificationActions from 'redux/modules/notification/notificationAction';

// ------------------------------------
// Constants
// ------------------------------------

const DYNAMIC_DISPATCH = '@@events/DYNAMIC_DISPATCH';
const DELETE_EVENTGROUP = '@@events/DELETE_EVENTGROUP';
const UPDATE_EVENTGROUP = '@@events/UPDATE_EVENTGROUP';

const dynamicDispatch = (params) => {
  return (dispatch) => {
    try {
      dispatch(createAction(DYNAMIC_DISPATCH)(params));
    } catch (error) {
      errorSvc.onError(error);
      dispatch(createAction(DYNAMIC_DISPATCH)(error));
    }
  };
};

const createEvent = (event) => {
  return async (dispatch) => {
    let eventData = {...event.attrs};
    if (eventData.pathCondition) {
      eventData.pathCondition = JSON.stringify(eventData.pathCondition);
    }
    delete eventData.id;
    delete eventData.modifiedDt;
    delete eventData.startingEl;
    dispatch(createAction(DYNAMIC_DISPATCH)(
      {path: 'eventCrudLoading', params: true, operation: 'update'}
    ));
    const response = await eventGroupSvc.createEventGroup(eventData);
    const actions = bindActionCreators(notificationActions, dispatch);
    if (response.ok) {
      let event = new Event(response.json.data);
      actions.updateNotification({type: 'add', name: event.attrs.name}, response.ok);
      dispatch(createAction(DYNAMIC_DISPATCH)(
        {path: 'events', params: event, operation: 'add'}
      ));
    } else {
      actions.updateNotification({}, response.ok);
      dispatch(createAction(DYNAMIC_DISPATCH)(new Error(JSON.stringify(response.error))));
    }
    dispatch(createAction(DYNAMIC_DISPATCH)(
      {path: 'eventCrudLoading', params: false, operation: 'update'}
    ));
  };
};

const deleteEvent = (event) => {
  return async (dispatch) => {
    dispatch(createAction(DYNAMIC_DISPATCH)(
      {path: 'eventCrudLoading', params: true, operation: 'update'}
    ));
    const response = await eventGroupSvc.deleteEventGroup({projectId: event.attrs.projectId, id: event.attrs.id});
    const actions = bindActionCreators(notificationActions, dispatch);

    if (response.ok) {
      actions.updateNotification({type: 'delete', name: event.attrs.name}, response.ok);
      if (event.attrs.eventType === 'PAGEVIEW') {
        dispatch(createAction(pageviewActionType.PAGEVIEW_EVENT_DELETED)({ id: event.attrs.id }));
      } else {
        dispatch(createAction(DELETE_EVENTGROUP)(
          {path: 'events', params: {id: event.attrs.id}, operation: 'delete'}
        ));
      }
    } else {
      actions.updateNotification({}, response.ok);
      dispatch(createAction(DYNAMIC_DISPATCH)(new Error(JSON.stringify(response.error))));
    }
    dispatch(createAction(DYNAMIC_DISPATCH)(
      {path: 'eventCrudLoading', params: false, operation: 'update'}
    ));
  };
};

const getEvents = (projectId) => {
  return async (dispatch, getState) => {
    const response = await eventGroupSvc.getEventGroups({projectId, type: 'CLICK'});
    if (response.ok) {
      let events = response.json.data
                    .filter(item => !item.isDeleted)
                    .map((attrs) => {
                      return new Event(attrs);
                    });
      dispatch(createAction(DYNAMIC_DISPATCH)(
        {path: 'events', params: events, operation: 'update'}
      ));
    } else {
      dispatch(createAction(DYNAMIC_DISPATCH)(new Error(JSON.stringify(response.error))));
    }
  };
};

const updateEvent = (event) => {
  return async (dispatch) => {
    let eventData = {...event.attrs, subType: 'EventGroup'};
    if (eventData.pathCondition) {
      eventData.pathCondition = JSON.stringify(eventData.pathCondition);
    }
    dispatch(createAction(DYNAMIC_DISPATCH)(
      {path: 'eventCrudLoading', params: true, operation: 'update'}
    ));
    delete eventData.startingEl;
    const response = await eventGroupSvc.updateEventGroup(eventData);
    const actions = bindActionCreators(notificationActions, dispatch);
    if (response.ok) {
      actions.updateNotification({type: 'update', name: event.attrs.name}, response.ok);
      dispatch(createAction(UPDATE_EVENTGROUP)({params: response.json.data, operation: 'update'}));
    } else {
      actions.updateNotification({}, response.ok);
      dispatch(createAction(DYNAMIC_DISPATCH)(new Error(JSON.stringify(response.error))));
    }
    dispatch(createAction(DYNAMIC_DISPATCH)(
      {path: 'eventCrudLoading', params: false, operation: 'update'}
    ));
  };
};

const actions = {
  dynamicDispatch,
  createEvent,
  deleteEvent,
  getEvents,
  updateEvent
};

const initState = {
  isLoading: false,
  updateStatus: {info: 'Your changes have been saved!', success: true},
  mode: MODE.EDIT,
  selectionType: 'single',
  showEventList: false,
  events: []
};

// ------------------------------------
// Reducer
// ----------------------------------
const reducer = handleActions({

  [DYNAMIC_DISPATCH]: (state, { payload, error }) => {
    if (error) {
      return state;
    }

    let operation = payload.operation;
    let newItems = cloneDeep(state);
    let parserResult = findTargetByPath(payload.path, newItems);
    let targetName = parserResult.targetName;
    switch (operation) {
      case 'add':
        parserResult.targetObj[targetName].push(payload.params);
        return newItems;
      case 'update':
        parserResult.targetObj[targetName] = payload.params;
        return newItems;
      case 'delete':
        return newItems;
      case 'get':
        return newItems;
    }
    return state;
  },
  [DELETE_EVENTGROUP]: (state, { payload, error }) => {
    if (error) {
      return state;
    }

    let id = payload.params.id;
    let index = findIndex(state.events, (eventGroup) => {
      return eventGroup.attrs.id === id;
    });
    if (index >= 0) {
      let events = state.events.slice();
      events.splice(index, 1);
      state = {...state, events};
    }
    return state;
  },
  [UPDATE_EVENTGROUP]: (state, { payload, error }) => {
    if (error) {
      return state;
    }
    const newState = { ...state };
    let id = payload.params.id;

    let eventGroup = find(newState.events, (eventGroup) => {
      return eventGroup.id === id;
    });
    if (eventGroup) {
      eventGroup.attrs = payload.params;
      eventGroup.attrs.pathCondition = eventGroup.attrs.pathCondition ? JSON.parse(eventGroup.attrs.pathCondition) : [];
    }

    return newState;
  }
}, initState);

export {
  actions as editorStateActions,
  reducer as editorStateReducer
};



/** WEBPACK FOOTER **
 ** ./src/redux/modules/editorState.js
 **/